import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { X, Save } from 'lucide-react';

const FIELDS = [
  ['case_number', 'Case Number'],
  ['case_title', 'Case Title'],
  ['case_type', 'Case Type'],
  ['priority', 'Priority'],
  ['assignment_type', 'Assignment Type'],
  ['assignment_location', 'Location'],
  ['start_date', 'Start Date'],
  ['end_date', 'End Date'],
  ['case_due_date', 'Due Date'],
  ['scheduled_hours', 'Scheduled Hours'],
  ['surveillance_days', 'Surveillance Days'],
  ['client_name', 'Client Name'],
  ['client_email', 'Client Email'],
  ['client_phone', 'Client Phone'],
];

/**
 * Iter131j — Admin edit view for Case Acceptance Forms rendered as a
 * FULL-PAGE fixed overlay (previously a cramped modal). Every assignment
 * field is editable while status is PENDING / VIEWED / EXPIRED.
 */
const CaseAcceptanceEditModal = ({ form, open, onOpenChange, onSaved }) => {
  const token = localStorage.getItem('nexus_token');
  const [values, setValues] = useState(() => {
    const seed = {};
    FIELDS.forEach(([k]) => { seed[k] = form?.[k] || ''; });
    seed.case_description = form?.case_description || '';
    seed.special_instructions = form?.special_instructions || '';
    return seed;
  });
  const [saving, setSaving] = useState(false);

  if (!open || !form) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined));
      const r = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${form.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || 'Failed to save');
      toast({ title: 'Form updated' });
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto" data-testid="case-acceptance-edit-page">
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="nx-eyebrow">Case Acceptance</div>
            <h1 className="nx-h1 mt-1">Edit Case Acceptance Form</h1>
            <p className="nx-body mt-1">Form <span className="font-mono text-slate-300">{form.id}</span> · Editable while Pending / Viewed / Expired.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <X className="w-4 h-4 mr-2" /> Close
            </Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-acceptance-edit">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-white text-base">Assignment fields</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>
                <Input value={values[k] || ''} onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                       className="bg-slate-800 border-slate-700 text-white h-10"
                       data-testid={`edit-field-${k}`} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-white text-base">Narrative</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Case Description</Label>
              <Textarea value={values.case_description} rows={4}
                        onChange={(e) => setValues({ ...values, case_description: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Special Instructions</Label>
              <Textarea value={values.special_instructions} rows={4}
                        onChange={(e) => setValues({ ...values, special_instructions: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pb-10">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-acceptance-edit-footer">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseAcceptanceEditModal;
